import { Link, useRouterState } from "@tanstack/react-router";
import { detectCluster, getClusterTheme } from "@/lib/cluster";
import { Sun, Moon, Languages, Menu } from "lucide-react";
import { useEffect, useState, lazy, Suspense } from "react";
import { Button } from "@/components/ui/button";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetClose,
} from "@/components/ui/sheet";
import {
  todayISO,
  parseISO,
  MONTH_NAMES_ID,
  MONTH_SLUGS_ID,
  calendarYearPath,
  calendarMonthPath,
  calendarDatePath,
} from "@/lib/calendar/dates";
import { currentHijriYear, HIJRI_MONTHS, HIJRI_MONTHS_EN } from "@/lib/calendar/hijri";
import { HARI_PENTING, getHariPentingForMonth } from "@/data/hari-penting";
import { translateHariPenting } from "@/i18n/holidays";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { useLocale, stripLocalePath } from "@/i18n/LocaleContext";
const SiteSearch = lazy(() =>
  import("@/components/search/SiteSearch").then((m) => ({ default: m.SiteSearch })),
);

// Lazy-load the heavy desktop mega menu (Radix navigation-menu, hari-penting data,
// hijri data, etc.). Only desktop users see it, and even then only after idle /
// hover. Drops ~30KB from the critical bundle.
const MegaMenu = lazy(() => import("./MegaMenu").then((m) => ({ default: m.MegaMenu })));

export function Header() {
  const [theme, setTheme] = useState<"light" | "dark">("light");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [megaReady, setMegaReady] = useState(false);
  const { t, href, altHref, altLocale, locale } = useLocale();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const altTarget = altHref(stripLocalePath(pathname));

  useEffect(() => {
    const saved = (localStorage.getItem("theme") as "light" | "dark") || "light";
    setTheme(saved);
    document.documentElement.classList.toggle("dark", saved === "dark");
  }, []);

  // Defer mega-menu mount until after first paint / idle so it does not
  // contribute to LCP/TBT on mobile or first-time desktop loads.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.matchMedia && !window.matchMedia("(min-width: 768px)").matches) return;
    const ric = (window as any).requestIdleCallback as ((cb: () => void) => number) | undefined;
    if (ric) ric(() => setMegaReady(true));
    else setTimeout(() => setMegaReady(true), 600);
  }, []);

  function toggleTheme() {
    const next = theme === "light" ? "dark" : "light";
    setTheme(next);
    document.documentElement.classList.toggle("dark", next === "dark");
    localStorage.setItem("theme", next);
  }

  const today = parseISO(todayISO())!;
  const todayPath = href(calendarDatePath(today.y, today.m, today.d));
  const monthPath = href(calendarMonthPath(today.y, today.m));
  const yearPath = href(calendarYearPath(today.y));
  const monthName = t.common.months[today.m - 1];
  const monthsList = locale === "en" ? t.common.months : MONTH_NAMES_ID;
  const hijriMonthsList = locale === "en" ? HIJRI_MONTHS_EN : HIJRI_MONTHS;
  const hy = currentHijriYear();
  const years = [today.y - 1, today.y, today.y + 1, today.y + 2, today.y + 3];
  const hijriYears = [hy - 1, hy, hy + 1, hy + 2, hy + 3];
  const hariThisMonth = getHariPentingForMonth(today.m).slice(0, 8);
  // Rotating window so internal-link weight spreads across HARI_PENTING
  // instead of stacking on ibu/guru on every single page.
  const FEATURED = (() => {
    const N = 6;
    const total = HARI_PENTING.length;
    if (total <= N) return HARI_PENTING.slice(0, N);
    const start = ((today.m - 1) * N) % total;
    return Array.from({ length: N }, (_, i) => HARI_PENTING[(start + i) % total]);
  })();
  const TOOLS = [
    { to: "/tools/kalkulator-hari", ...t.tools.calculator },
    { to: "/tools/cek-weton", ...t.tools.weton },
    { to: "/tools/hari-jadian", ...t.tools.jadian },
    { to: "/tools/hari-lahir", ...t.tools.lahir },
    { to: "/tools/hari-menikah", ...t.tools.menikah },
    { to: "/tools/hari-putus", ...t.tools.putus },
    { to: "/tools/hari-sahabat", ...t.tools.sahabat },
    { to: "/tools/hari-ldr", ...t.tools.ldr },
  ];
  const PRAYER_CITIES: Array<[string, string]> = [
    ["jakarta-pusat", "Jakarta"], ["bandung", "Bandung"], ["surabaya", "Surabaya"],
    ["medan", "Medan"], ["makassar", "Makassar"], ["yogyakarta", "Yogyakarta"],
    ["semarang", "Semarang"], ["denpasar", "Denpasar"],
  ];

  return (
    <header className="sticky top-0 z-40 w-full border-b border-border bg-background/90 backdrop-blur">
      <div className="container mx-auto flex h-16 items-center justify-between gap-2 px-3 sm:h-14 sm:px-4">
        <Link
          to={href("/") as never}
          className="flex shrink-0 items-center gap-2 font-semibold"
          aria-label="hari.co.id"
        >
          {/* Mobile: mark + tight wordmark text. Desktop: full SVG lockup. */}
          <span className="flex items-center gap-2 sm:hidden">
            <img
              src="/logo-mark.svg"
              alt="Logo hari.co.id"
              width={36}
              height={36}
              className="h-9 w-9"
            />
            <span className="flex flex-col leading-none">
              <span className="font-display text-base text-primary">hari.co.id</span>
              <span className="text-[9px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                {locale === "en" ? "Indonesian Calendar" : "Kalender Indonesia"}
              </span>
            </span>
          </span>
          <img
            src="/logo.svg"
            alt="hari.co.id — Kalender Indonesia"
            width={140}
            height={36}
            className="hidden h-9 w-auto sm:block"
          />
          <span className="sr-only">
            {locale === "en" ? "Home — Indonesian Calendar" : "Beranda — Kalender Indonesia"}
          </span>
        </Link>

        <div
          className="hidden md:block"
          onMouseEnter={() => setMegaReady(true)}
          onFocus={() => setMegaReady(true)}
        >
          {megaReady ? (
            <Suspense fallback={<MegaMenuFallback />}>
              <MegaMenu />
            </Suspense>
          ) : (
            <MegaMenuFallback />
          )}
        </div>

        <div className="flex min-w-0 items-center gap-0.5 sm:gap-1">
          <Suspense fallback={null}>
            <SiteSearch />
          </Suspense>
          {/* Always visible — even on phones, BEFORE the hamburger */}
          <Link
            to={altTarget as never}
            aria-label={t.langSwitch.label}
            className="flex items-center gap-1 rounded-md px-2 py-1 text-xs font-semibold hover:bg-accent"
          >
            <Languages className="h-3.5 w-3.5" />
            {altLocale.toUpperCase()}
          </Link>
          <Button variant="ghost" size="icon" onClick={toggleTheme} aria-label="Theme">
            {theme === "light" ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
          </Button>

          {/* Mobile sheet trigger */}
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="md:hidden" aria-label={t.nav.menu}>
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[88vw] max-w-sm overflow-y-auto p-0">
              <SheetHeader className="border-b border-border px-4 py-3">
                <SheetTitle className="font-display text-xl">hari.co.id</SheetTitle>
              </SheetHeader>
              <nav className="flex flex-col gap-1 p-3 text-[15px]">
                <SheetClose asChild>
                  <Link to={todayPath as never} className="rounded-md bg-primary/10 px-3 py-2.5 font-medium text-primary">
                    {t.nav.today}
                  </Link>
                </SheetClose>
                <SheetClose asChild>
                  <Link to={monthPath as never} className="rounded-md px-3 py-2.5 hover:bg-accent">
                    {t.nav.monthNow} — {monthName} {today.y}
                  </Link>
                </SheetClose>
                <SheetClose asChild>
                  <Link to={yearPath as never} className="rounded-md px-3 py-2.5 hover:bg-accent">
                    {t.nav.yearOf(today.y)}
                  </Link>
                </SheetClose>
                <div className="my-2 h-px bg-border" />

                <Accordion type="multiple" className="w-full">
                  {/* Tahun (mirror desktop YEAR mega) */}
                  <AccordionItem value="tahun">
                    <AccordionTrigger className="px-3 py-2.5 text-sm font-medium hover:no-underline">
                      {t.nav.year}
                    </AccordionTrigger>
                    <AccordionContent className="px-2 pb-2">
                      <div className="grid grid-cols-3 gap-1">
                        {years.map((yy) => (
                          <SheetClose asChild key={yy}>
                            <Link
                              to={href(calendarYearPath(yy)) as never}
                              className={
                                "rounded-md border border-border px-2 py-2 text-center text-xs hover:bg-accent " +
                                (yy === today.y ? "bg-primary/10 font-semibold text-primary" : "")
                              }
                            >
                              {yy}
                            </Link>
                          </SheetClose>
                        ))}
                      </div>
                      <p className="mt-2 px-1 text-[10px] font-semibold uppercase text-muted-foreground">
                        {t.mega.monthsHeading} {today.y}
                      </p>
                      <div className="mt-1 grid grid-cols-3 gap-1">
                        {MONTH_SLUGS_ID.map((s: string, i: number) => (
                          <SheetClose asChild key={s}>
                            <Link
                              to={href(`/kalender/${today.y}/${s}`) as never}
                              className={
                                "rounded px-2 py-1 text-center text-[11px] hover:bg-accent " +
                                (i + 1 === today.m ? "bg-primary/10 font-semibold text-primary" : "")
                              }
                            >
                              {monthsList[i].slice(0, 3)}
                            </Link>
                          </SheetClose>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                  {/* Kalkulator (mirror TOOLS) */}
                  <AccordionItem value="tools">
                    <AccordionTrigger className="px-3 py-2.5 text-sm font-medium hover:no-underline">
                      {t.mega.calculatorTitle}
                    </AccordionTrigger>
                    <AccordionContent className="px-2 pb-2">
                      <div className="grid gap-1">
                        {TOOLS.map((tool) => (
                          <SheetClose asChild key={tool.to}>
                            <Link to={href(tool.to) as never} className="rounded-md px-2 py-1.5 text-sm hover:bg-accent">
                              <span className="font-medium">{tool.label}</span>
                              {tool.desc && <span className="block text-[11px] text-muted-foreground">{tool.desc}</span>}
                            </Link>
                          </SheetClose>
                        ))}
                        <SheetClose asChild>
                          <Link to={href("/tools") as never} className="mt-1 rounded-md border border-border px-2 py-1.5 text-center text-xs hover:bg-accent">
                            {t.mega.seeAllCalculators}
                          </Link>
                        </SheetClose>
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                  {/* Hijri (mirror) */}
                  <AccordionItem value="hijri">
                    <AccordionTrigger className="px-3 py-2.5 text-sm font-medium hover:no-underline">
                      {t.nav.hijri}
                    </AccordionTrigger>
                    <AccordionContent className="px-2 pb-2">
                      <div className="grid grid-cols-3 gap-1">
                        {hijriYears.map((hyy) => (
                          <SheetClose asChild key={hyy}>
                            <Link
                              to={href(`/kalender-hijriah/${hyy}/`) as never}
                              className={
                                "rounded-md border border-border px-2 py-2 text-center text-xs hover:bg-accent " +
                                (hyy === hy ? "bg-primary/10 font-semibold text-primary" : "")
                              }
                            >
                              {hyy}H
                            </Link>
                          </SheetClose>
                        ))}
                      </div>
                      <p className="mt-2 px-1 text-[10px] font-semibold uppercase text-muted-foreground">
                        {t.mega.monthsHeading} {hy}H
                      </p>
                      <div className="mt-1 grid grid-cols-2 gap-1">
                        {hijriMonthsList.map((name, i) => (
                          <SheetClose asChild key={name}>
                            <Link
                              to={href(`/kalender-hijriah/${hy}/${i + 1}/`) as never}
                              className="rounded px-2 py-1 text-[11px] hover:bg-accent"
                            >
                              {name}
                            </Link>
                          </SheetClose>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                  {/* Hari Penting (mirror) */}
                  <AccordionItem value="hari">
                    <AccordionTrigger className="px-3 py-2.5 text-sm font-medium hover:no-underline">
                      {t.nav.important}
                    </AccordionTrigger>
                    <AccordionContent className="px-2 pb-2">
                      <p className="px-1 text-[10px] font-semibold uppercase text-muted-foreground">
                        {t.mega.monthOfX(monthName)}
                      </p>
                      <div className="mt-1 grid gap-0.5">
                        {hariThisMonth.map((h) => (
                          <SheetClose asChild key={h.slug}>
                            <Link to={`/hari/${h.slug}` as never} className="rounded px-2 py-1 text-xs hover:bg-accent">
                              {translateHariPenting(h.name, locale)}
                            </Link>
                          </SheetClose>
                        ))}
                      </div>
                      <p className="mt-2 px-1 text-[10px] font-semibold uppercase text-muted-foreground">{t.mega.popular}</p>
                      <div className="mt-1 grid gap-0.5">
                        {FEATURED.map((h) => (
                          <SheetClose asChild key={h.slug}>
                            <Link to={`/hari/${h.slug}` as never} className="rounded px-2 py-1 text-xs hover:bg-accent">
                              {translateHariPenting(h.name, locale)}
                            </Link>
                          </SheetClose>
                        ))}
                        <SheetClose asChild>
                          <Link to={href("/hari") as never} className="mt-1 rounded-md border border-border px-2 py-1 text-center text-xs hover:bg-accent">
                            {t.mega.seeAllImportant}
                          </Link>
                        </SheetClose>
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                  {/* Jadwal Sholat (mirror) */}
                  <AccordionItem value="sholat">
                    <AccordionTrigger className="px-3 py-2.5 text-sm font-medium text-prayer hover:no-underline">
                      Jadwal Sholat
                    </AccordionTrigger>
                    <AccordionContent className="px-2 pb-2">
                      <div className="grid gap-1">
                        <SheetClose asChild>
                          <Link to={"/jadwal-sholat" as never} className="rounded-md px-2 py-1.5 text-sm hover:bg-prayer/10">
                            <span className="font-medium">Jadwal Sholat Hari Ini</span>
                            <span className="block text-[11px] text-muted-foreground">Otomatis sesuai lokasi</span>
                          </Link>
                        </SheetClose>
                        <SheetClose asChild>
                          <Link to={"/jadwal-imsak" as never} className="rounded-md px-2 py-1.5 text-sm hover:bg-prayer/10">
                            <span className="font-medium">Jadwal Imsakiyah</span>
                            <span className="block text-[11px] text-muted-foreground">Sahur & berbuka puasa Ramadan</span>
                          </Link>
                        </SheetClose>
                        <SheetClose asChild>
                          <Link to={"/jadwal-sholat/lokasi" as never} className="rounded-md px-2 py-1.5 text-sm hover:bg-prayer/10">
                            <span className="font-medium">Indeks Lokasi</span>
                            <span className="block text-[11px] text-muted-foreground">Semua provinsi, kota, kecamatan</span>
                          </Link>
                        </SheetClose>
                      </div>
                      <p className="mt-2 px-1 text-[10px] font-semibold uppercase text-muted-foreground">Kota Populer</p>
                      <div className="mt-1 grid grid-cols-2 gap-1">
                        {PRAYER_CITIES.map(([slug, label]) => (
                          <SheetClose asChild key={slug}>
                            <Link to={`/jadwal-sholat/${slug}` as never} className="rounded px-2 py-1 text-xs hover:bg-prayer/10 hover:text-prayer">
                              {label}
                            </Link>
                          </SheetClose>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>

                <div className="my-2 h-px bg-border" />
                <SheetClose asChild>
                  <Link to={href(`/tanggal-merah/${today.y}`) as never} className="rounded-md px-3 py-2.5 hover:bg-accent">{t.common.redDates} {today.y}</Link>
                </SheetClose>
                <div className="my-2 h-px bg-border" />
                <SheetClose asChild>
                  <Link to={href("/kontak") as never} className="rounded-md px-3 py-2.5 hover:bg-accent">{t.nav.contact}</Link>
                </SheetClose>
                <SheetClose asChild>
                  <Link
                    to={altTarget as never}
                    className="flex items-center gap-2 rounded-md px-3 py-2.5 text-xs font-medium hover:bg-accent"
                  >
                    <Languages className="h-4 w-4" /> {t.langSwitch.label} ({altLocale.toUpperCase()})
                  </Link>
                </SheetClose>
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}

/**
 * Lightweight static nav rendered before/instead of the heavy MegaMenu.
 * Keeps SSR HTML stable (no layout shift) and provides the same key links
 * for crawlers — full mega menu mounts on hover/focus or after idle.
 */
function MegaMenuFallback() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const active = detectCluster(pathname);
  const cls = (k: typeof active) => {
    const theme = getClusterTheme(k);
    return active === k
      ? `rounded-md px-3 py-1.5 ${theme.accentBg} ${theme.accentText} font-semibold`
      : `rounded-md px-3 py-1.5 ${theme.hover}`;
  };
  return (
    <nav aria-label="Menu utama" className="flex items-center gap-1 text-sm">
      <Link to={"/kalender" as never} className={cls("calendar")}>Kalender</Link>
      <Link to={"/kalender-hijriah" as never} className={cls("hijri")}>Hijriah</Link>
      <Link to={"/jadwal-sholat" as never} className={cls("prayer")}>Jadwal Sholat</Link>
      <Link to={"/tools" as never} className={cls("tools")}>Tools</Link>
      <Link to={"/hari" as never} className={cls("important")}>Hari Penting</Link>
    </nav>
  );
}